import { create } from 'zustand';
import type { ReportListItem } from './reportStore';

export interface Team extends ReportListItem {
  id: number; // index within current report list
  selectedTeams: number | null;
  klasse: number;
  /** Golf club this Spielgemeinschaft is entered with; only for Spielgemeinschaft leagues. */
  spielgemeinschaftClub?: string;
}

export interface FinalData {
  transaction_id: number;
  date: string;
  pdf_url: string;
}

interface TeamRegistrationState {
  title: string;
  editId: number | null;
  pdfUrl: string | null;
  reportId: number;
  teams: Team[]; // derived from ReportListItem selections in TeamTab
  iban: string;
  invoiceDate: string;
  reporterName: string;
  finalData: FinalData | null;
}

interface TeamRegistrationActions {
  setTitle: (title: string) => void;
  setReportId: (reportId: number) => void;
  setEditId: (editId: number | null) => void;
  setPdfUrl: (pdfUrl: string) => void;
  setTeams: (teams: Team[]) => void;
  getTeams: () => Team[];

  updateTeamSelectedTeams: (
    teamId: number,
    selectedTeams: number | null,
  ) => void;
  setSpielgemeinschaftClub: (teamId: number, clubName: string) => void;

  setIban: (iban: string) => void;

  setReporterName: (name: string) => void;
  getReporterName: () => string;

  setInvoiceDate: (date: string) => void;
  getInvoiceDate: () => string;

  setFinalData: (data: FinalData) => void;

  reset: () => void;

  getReport: () => {
    reportId: number;
    teams: Team[];
    iban: string;
    invoiceDate: string;
    reporterName: string;
  };
}

type TeamRegistrationStore = TeamRegistrationState & TeamRegistrationActions;

const initialState: TeamRegistrationState = {
  title: '',
  reportId: 0,
  editId: null,
  pdfUrl: null,
  teams: [],
  iban: '',
  invoiceDate: '',
  reporterName: 'Milad Davoodabadi',
  finalData: null,
};

export const useTeamRegistrationStore = create<TeamRegistrationStore>(
  (set, get) => ({
    ...initialState,

    setTitle: (title: string) => {
      set({ title });
    },

    setReportId: (reportId: number) => {
      set({ reportId });
    },

    setEditId: (editId: number | null) => {
      set({ editId });
    },

    setPdfUrl: (pdfUrl: string) => {
      set({ pdfUrl });
    },

    setTeams: (teams: Team[]) => {
      set({ teams });
    },

    getTeams: () => {
      return get().teams;
    },

    updateTeamSelectedTeams: (teamId: number, selectedTeams: number | null) => {
      set((state) => ({
        teams: state.teams.map((team) =>
          team.id === teamId ? { ...team, selectedTeams } : team,
        ),
      }));
    },

    setSpielgemeinschaftClub: (teamId: number, clubName: string) => {
      set((state) => ({
        teams: state.teams.map((team) =>
          team.id === teamId
            ? { ...team, spielgemeinschaftClub: clubName }
            : team,
        ),
      }));
    },

    setIban: (iban: string) => {
      set({ iban });
    },

    setReporterName: (name: string) => {
      set({ reporterName: name });
    },

    setInvoiceDate: (date: string) => {
      set({ invoiceDate: date });
    },

    getReporterName: () => {
      return get().reporterName;
    },

    setFinalData: (data: FinalData) => {
      set({ finalData: data });
    },

    getInvoiceDate: () => {
      return get().invoiceDate;
    },

    reset: () => {
      set({
        title: '',
        reportId: 0,
        editId: null,
        pdfUrl: null,
        teams: [],
        reporterName: '',
        finalData: null,
      });
    },

    getReport: () => {
      return {
        reportId: get().reportId,
        teams: get().teams,
        iban: get().iban,
        invoiceDate: get().invoiceDate,
        reporterName: get().reporterName,
      };
    },
  }),
);
